import React, { forwardRef } from 'react'

import * as ES from './expandableStyles'
import * as S from './styles'

import { FormItem } from '../FormItem'

import type { ToggleWithExpandableProps } from './types'

/**
 * Form toggle component with expandable content
 * @component
 * @param {string} label - label at the right side of the toggle
 * @param {string} name - form field key
 * @param {boolean} isLocked - input disabled and lock icon is visible
 * @param {React.ReactNode} expandableContent - content to show when toggle is active
 * @example
 * import { useForm } from 'react-hook-form'
 * const { register } = useForm()
 *
 * <ToggleWithExpandable
 *   {...register('hasAccessibilityNeeds')}
 *   label="Accessibility Requirements"
 *   expandableContent={<TextArea {...register('accessibilityDetails')} />}
 * />
 */

export const ToggleWithExpandable: React.FC<ToggleWithExpandableProps> =
  forwardRef<HTMLInputElement, ToggleWithExpandableProps>(
    (
      {
        className,
        label,
        name,
        isLocked = false,
        disabled,
        expandableContent,
        checked,
        ...restProps
      },
      ref
    ) => (
      <FormItem>
        <ES.ToggleWithExpandableWrapper>
          <ES.ExpandableToggleContainer
            $disabled={disabled}
            className={className}
            $isExpanded={checked}
          >
            <S.Toggle>
              <input
                ref={ref}
                name={name}
                type="checkbox"
                checked={checked}
                disabled={isLocked || disabled}
                {...restProps}
              />
              <span />
              {isLocked && <S.LockIcon />}
            </S.Toggle>
            <S.ToggleCaptionContainer>{label}</S.ToggleCaptionContainer>
          </ES.ExpandableToggleContainer>

          {expandableContent && (
            <ES.ExpandableContent $isExpanded={checked} $disabled={disabled}>
              <ES.ExpandableContentInner $isExpanded={checked}>
                {checked ? expandableContent : null}
              </ES.ExpandableContentInner>
            </ES.ExpandableContent>
          )}
        </ES.ToggleWithExpandableWrapper>
      </FormItem>
    )
  )

ToggleWithExpandable.displayName = 'ToggleWithExpandable'
